export const API_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

export const TRIGGER_EVENTS = [
  {
    value: "machine.offline",
    label: "Makine çevrimdışı oldu",
    eventType: "machine_status",
  },
  {
    value: "machine.online",
    label: "Makine tekrar çevrimiçi oldu",
    eventType: "machine_status",
  },
  {
    value: "approval.pending",
    label: "Onay bekleyen adım oluştu",
    eventType: "approval",
  },
  {
    value: "approval.step_approved",
    label: "Onay adımı onaylandı",
    eventType: "approval",
  },
  {
    value: "approval.completed",
    label: "Onay akışı tamamlandı",
    eventType: "approval",
  },
  {
    value: "approval.rejected",
    label: "Onay reddedildi",
    eventType: "approval",
  },
  {
    value: "scrap.alert",
    label: "Hurda kaydı eklendi",
    eventType: "scrap_alert",
  },
  {
    value: "stock.alert",
    label: "Stok kritik seviyede",
    eventType: "stock_alert",
  },
  {
    value: "shift.stop",
    label: "Makinede duruş tespit edildi",
    eventType: "machine_stop",
  },
  {
    value: "production.daily_summary",
    label: "Günlük üretim özeti",
    eventType: "production",
  },
  {
    value: "production.target_missed",
    label: "Üretim hedefi tutmadı",
    eventType: "production",
  },
];

export const TRIGGER_EVENT_LABELS = Object.fromEntries(
  TRIGGER_EVENTS.map((e) => [e.value, e.label]),
);

export const EVENT_TYPES = [
  "machine_status",
  "machine_stop",
  "approval",
  "scrap_alert",
  "stock_alert",
  "production",
  "custom",
];

export const SEVERITIES = ["info", "warning", "critical"];

export const EVENT_TYPE_LABELS = {
  machine_status: "Makine durumu",
  machine_stop: "Makine duruşu",
  approval: "Onay işlemleri",
  scrap_alert: "Hurda uyarısı",
  stock_alert: "Stok uyarısı",
  production: "Üretim",
  custom: "Diğer",
};

export const SEVERITY_LABELS = {
  info: "Bilgi",
  warning: "Uyarı",
  critical: "Kritik",
};

export const SEVERITY_LOZENGE = {
  info: "primary-subtle",
  warning: "warning-subtle",
  critical: "danger-subtle",
};

export const CHANNEL_LABELS = {
  inapp: "Uygulama içi",
  email: "E-posta",
  push: "Anlık bildirim",
};

export const TARGET_TYPE_LABELS = {
  all: "Tüm kullanıcılar",
  approval_admins: "Onay yetkilileri",
  event_users: "Akıştaki ilgili kullanıcılar",
  profiles: "Seçili profiller",
  users: "Belirli kullanıcılar",
};

export const OPERATOR_LABELS = {
  eq: "eşittir (=)",
  gte: "≥ eşik ve üzeri",
  lte: "≤ eşik ve altı",
  contains: "içerir",
};

/** Numeric fields eligible for gte/lte */
export const NUMERIC_CONDITION_FIELDS = new Set([
  "scrapRate",
  "quantity",
  "percentage",
  "currentStock",
  "minStock",
  "actual",
  "target",
  "durationMinutes",
]);

const TEXT_CONDITION_FIELDS = new Set([
  "machineName",
  "location",
  "status",
  "stage",
  "entityType",
  "companyName",
  "partName",
  "planTitle",
  "stopReason",
  "shiftName",
]);

export function getDefaultOperatorForField(field, triggerEvent) {
  if (field === "actual" && triggerEvent === "production.target_missed")
    return "lte";
  if (field === "actual" && triggerEvent === "production.daily_summary")
    return "gte";
  if (["currentStock", "minStock", "percentage"].includes(field)) return "lte";
  if (field === "durationMinutes") return "gte";
  if (NUMERIC_CONDITION_FIELDS.has(field)) return "gte";
  return "eq";
}

export function getOperatorsForField(field) {
  if (field === "durationMinutes") {
    return {
      eq: OPERATOR_LABELS.eq,
      gte: OPERATOR_LABELS.gte,
    };
  }
  if (NUMERIC_CONDITION_FIELDS.has(field)) {
    return {
      eq: OPERATOR_LABELS.eq,
      gte: OPERATOR_LABELS.gte,
      lte: OPERATOR_LABELS.lte,
    };
  }
  if (TEXT_CONDITION_FIELDS.has(field)) {
    return { eq: OPERATOR_LABELS.eq, contains: OPERATOR_LABELS.contains };
  }
  return OPERATOR_LABELS;
}

export const CONDITION_FIELD_HINTS = {
  scrapRate: "Yüksek hurda uyarısı: «≥ eşik ve üzeri», örn. 20 → %20 ve üzeri.",
  quantity: "Belirli adetten fazla hurda: «≥ eşik ve üzeri».",
  currentStock:
    "Kritik stok uyarısı: «≤ eşik ve altı», örn. 5 → stok 5 ve altı.",
  minStock: "Minimum stok eşiği: «≤ eşik ve altı».",
  percentage:
    "Hedefin altında kalan üretim: «≤ eşik ve altı», örn. 70 → %70 ve altı.",
  actual:
    "Üretim miktarı eşiği — günlük özet için «≥», hedef tutmadı için «≤».",
  durationMinutes:
    "Süre (dakika). Uzun duruş veya çevrimdışı süresi için «≥ eşik ve üzeri», örn. 30 → 30 dk ve üzeri.",
  stage: "Geçerli değerler: admin veya user.",
  status: "Makine durumu: çevrimiçi veya çevrimdışı.",
  entityType: "Kayıt türü: order.",
};

export const EVENT_VARIABLES = {
  machine_status: [
    { key: "machineName", label: "Makine adı" },
    { key: "machineId", label: "Makine numarası" },
    { key: "status", label: "Durum" },
    { key: "location", label: "Konum" },
    { key: "shiftName", label: "Vardiya adı" },
    { key: "notes", label: "Açıklama" },
  ],
  machine_stop: [
    { key: "machineName", label: "Makine adı" },
    { key: "machineId", label: "Makine numarası" },
    { key: "location", label: "Konum" },
    { key: "shiftName", label: "Vardiya adı" },
    { key: "started", label: "Duruş başlangıcı" },
    { key: "stop", label: "Duruş bitişi" },
    { key: "stopReason", label: "Duruş nedeni" },
    { key: "durationMinutes", label: "Duruş süresi (dk)" },
    { key: "notes", label: "Açıklama" },
    { key: "status", label: "Durum" },
  ],
  approval: [
    { key: "companyName", label: "Firma adı" },
    { key: "orderName", label: "Teklif numarası" },
    { key: "typeLabel", label: "Kayıt türü" },
    { key: "step", label: "Onay adımı" },
    { key: "stepNumber", label: "Adım sırası" },
    { key: "stage", label: "Onay aşaması" },
    { key: "totalAmount", label: "Toplam tutar" },
    { key: "totalDevices", label: "Toplam cihaz" },
    { key: "sourcePage", label: "Kaynak sayfa" },
    { key: "reason", label: "Ret nedeni" },
  ],
  scrap_alert: [
    { key: "machineName", label: "Makine adı" },
    { key: "partName", label: "Parça adı" },
    { key: "quantity", label: "Hurda adedi" },
    { key: "totalRejected", label: "Toplam hurda" },
    { key: "scrapRate", label: "Hurda oranı (%)" },
  ],
  stock_alert: [
    { key: "partName", label: "Parça adı" },
    { key: "currentStock", label: "Mevcut stok" },
    { key: "minStock", label: "Minimum stok" },
  ],
  production: [
    { key: "date", label: "Tarih" },
    { key: "planTitle", label: "Plan adı" },
    { key: "actual", label: "Gerçekleşen miktar" },
    { key: "target", label: "Hedef miktar" },
    { key: "percentage", label: "Tamamlanma oranı (%)" },
    { key: "machineName", label: "Makine adı" },
  ],
  custom: [],
};

export const CONDITION_FIELD_OPTIONS = [
  { value: "machineName", label: "Makine adı" },
  { value: "location", label: "Konum" },
  { value: "status", label: "Durum" },
  { value: "stopReason", label: "Duruş nedeni" },
  { value: "durationMinutes", label: "Duruş süresi (dk)" },
  { value: "shiftName", label: "Vardiya adı" },
  { value: "entityType", label: "Kayıt türü" },
  { value: "stage", label: "Onay aşaması" },
  { value: "companyName", label: "Firma adı" },
  { value: "scrapRate", label: "Hurda oranı (%)" },
  { value: "quantity", label: "Miktar" },
  { value: "partName", label: "Parça adı" },
  { value: "currentStock", label: "Mevcut stok" },
  { value: "minStock", label: "Minimum stok" },
  { value: "percentage", label: "Tamamlanma oranı (%)" },
  { value: "actual", label: "Gerçekleşen miktar" },
  { value: "target", label: "Hedef miktar" },
  { value: "planTitle", label: "Plan adı" },
];

export const CONDITION_FIELDS_BY_TRIGGER = {
  "machine.offline": [
    { value: "machineName", label: "Makine adı" },
    { value: "location", label: "Konum" },
    { value: "status", label: "Durum" },
    { value: "durationMinutes", label: "Çevrimdışı süresi (dk)" },
  ],
  "machine.online": [
    { value: "machineName", label: "Makine adı" },
    { value: "location", label: "Konum" },
    { value: "status", label: "Durum" },
    { value: "durationMinutes", label: "Çevrimdışı kaldığı süre (dk)" },
  ],
  "shift.stop": [
    { value: "machineName", label: "Makine adı" },
    { value: "location", label: "Konum" },
    { value: "stopReason", label: "Duruş nedeni" },
    { value: "shiftName", label: "Vardiya adı" },
    { value: "durationMinutes", label: "Duruş süresi (dk)" },
  ],
  "approval.pending": [
    { value: "stage", label: "Onay aşaması" },
    { value: "entityType", label: "Kayıt türü" },
    { value: "companyName", label: "Firma adı" },
  ],
  "approval.step_approved": [
    { value: "entityType", label: "Kayıt türü" },
    { value: "companyName", label: "Firma adı" },
  ],
  "approval.rejected": [
    { value: "entityType", label: "Kayıt türü" },
    { value: "companyName", label: "Firma adı" },
  ],
  "approval.completed": [
    { value: "entityType", label: "Kayıt türü" },
    { value: "companyName", label: "Firma adı" },
  ],
  "scrap.alert": [
    { value: "machineName", label: "Makine adı" },
    { value: "scrapRate", label: "Hurda oranı (%)" },
    { value: "quantity", label: "Miktar" },
    { value: "partName", label: "Parça adı" },
  ],
  "stock.alert": [
    { value: "partName", label: "Parça adı" },
    { value: "currentStock", label: "Mevcut stok" },
    { value: "minStock", label: "Minimum stok" },
  ],
  "production.daily_summary": [
    { value: "machineName", label: "Makine adı" },
    { value: "actual", label: "Gerçekleşen miktar" },
  ],
  "production.target_missed": [
    { value: "percentage", label: "Tamamlanma oranı (%)" },
    { value: "actual", label: "Gerçekleşen miktar" },
    { value: "target", label: "Hedef miktar" },
    { value: "planTitle", label: "Plan adı" },
  ],
};

/** Approval events pick their own recipients from the workflow steps. */
export function isWorkflowOwnedEvent(triggerEvent) {
  return String(triggerEvent || "").startsWith("approval.");
}

export function getConditionFieldsForEvent(triggerEvent) {
  if (!triggerEvent) return CONDITION_FIELD_OPTIONS;
  return CONDITION_FIELDS_BY_TRIGGER[triggerEvent] || CONDITION_FIELD_OPTIONS;
}

export const PREFERENCE_EVENT_TYPES = [
  {
    key: "machine_status",
    label: "Makine durumu",
    description: "Çevrimiçi / çevrimdışı bildirimleri",
  },
  {
    key: "machine_stop",
    label: "Makine duruşu",
    description: "Makinede duruş tespit edildiğinde",
  },
  {
    key: "approval",
    label: "Onay işlemleri",
    description: "Bekleyen teklif ve sipariş onayları",
  },
  {
    key: "scrap_alert",
    label: "Hurda uyarıları",
    description: "Hurda kayıtları",
  },
  {
    key: "stock_alert",
    label: "Stok uyarıları",
    description: "Kritik stok seviyeleri",
  },
  {
    key: "production",
    label: "Üretim",
    description: "Günlük özet ve hedef bildirimleri",
  },
  { key: "custom", label: "Diğer", description: "Özel bildirimler" },
];

export function getTriggerEventLabel(value) {
  if (!value) return "—";
  return TRIGGER_EVENT_LABELS[value] || String(value).replace(/\./g, " · ");
}

export function getEventTypeLabel(value) {
  if (!value) return "—";
  return EVENT_TYPE_LABELS[value] || "Diğer";
}

export function getSeverityLabel(value) {
  if (!value) return "—";
  return SEVERITY_LABELS[value] || "Bilgi";
}

export function getTargetTypeLabel(value) {
  if (!value) return "—";
  return TARGET_TYPE_LABELS[value] || "Belirtilmemiş";
}

export function getChannelLabel(value) {
  return CHANNEL_LABELS[value] || value;
}

export function formatChannels(channels) {
  return (channels || []).map((c) => getChannelLabel(c)).join(", ") || "—";
}

export function getVariableHintList(eventType) {
  const vars = EVENT_VARIABLES[eventType] || [];
  if (!vars.length) return "";
  return vars.map((v) => v.label).join(", ");
}

export function getVariableInsertList(eventType) {
  const vars = EVENT_VARIABLES[eventType] || [];
  return vars.map((v) => `{{${v.key}}}`);
}

/** Example values shown in template preview */
export const SAMPLE_VARIABLE_VALUES = {
  machineName: "Makine 1",
  machineId: "M001",
  status: "duruş",
  location: "Hat A",
  shiftName: "Sabah vardiyası",
  notes: "Üretim durdu",
  started: "30.07.2026 14:32",
  stop: "Devam ediyor",
  stopReason: "Arıza",
  durationMinutes: "45",
  orderId: "12345",
  orderName: "GT/0626/2",
  companyName: "GlobalTech",
  typeLabel: "Teklif",
  totalAmount: "125.000",
  totalDevices: "12",
  step: "Kontrol Onayı",
  stepNumber: "1",
  stage: "Yönetici onayı",
  sourcePage: "Onaylar",
  reason: "Bilgiler eksik",
  partName: "Parça A",
  quantity: "5",
  totalRejected: "12",
  scrapRate: "8",
  currentStock: "0",
  minStock: "10",
  date: "23.06.2026",
  planTitle: "Haftalık üretim planı",
  actual: "450",
  target: "500",
  percentage: "90",
  lineId: "Hat 1",
  planId: "PLN-001",
};

function escapeRegex(str) {
  return String(str).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Convert stored template → user-friendly [Firma adı] tokens */
export function toFriendlyTemplate(text, eventType) {
  if (!text) return "";
  let result = text;
  const vars = EVENT_VARIABLES[eventType] || [];
  for (const v of vars) {
    result = result.replace(
      new RegExp(`\\{\\{${v.key}\\}\\}`, "g"),
      `[${v.label}]`,
    );
  }
  return result;
}

/** Convert user-friendly text → backend {{companyName}} format */
export function toBackendTemplate(text, eventType) {
  if (!text) return "";
  let result = text;
  const vars = EVENT_VARIABLES[eventType] || [];
  for (const v of vars) {
    result = result.replace(
      new RegExp(`\\[${escapeRegex(v.label)}\\]`, "g"),
      `{{${v.key}}}`,
    );
  }
  return result;
}

export function insertVariableToken(text, label, cursorStart, cursorEnd) {
  const token = `[${label}]`;
  const start = cursorStart ?? (text || "").length;
  const end = cursorEnd ?? start;
  const before = (text || "").slice(0, start);
  const after = (text || "").slice(end);
  const needsSpaceBefore = before.length > 0 && !/\s$/.test(before);
  const insert = `${needsSpaceBefore ? " " : ""}${token}`;
  const newText = before + insert + after;
  return { text: newText, cursor: before.length + insert.length };
}

export function previewTemplateText(text, eventType) {
  if (!text) return "";
  let result = text;
  const vars = EVENT_VARIABLES[eventType] || [];
  const sampleValues =
    eventType === "machine_status"
      ? { ...SAMPLE_VARIABLE_VALUES, status: "Çevrimdışı" }
      : SAMPLE_VARIABLE_VALUES;
  for (const v of vars) {
    const sample = sampleValues[v.key] ?? v.label;
    result = result.replace(
      new RegExp(`\\[${escapeRegex(v.label)}\\]`, "g"),
      sample,
    );
  }
  // Legacy templates still using {{key}}
  for (const v of vars) {
    const sample = sampleValues[v.key] ?? v.label;
    result = result.replace(new RegExp(`\\{\\{${v.key}\\}\\}`, "g"), sample);
  }
  return result.replace(
    /\{\{(\w+)\}\}/g,
    (_, key) => sampleValues[key] || "",
  );
}

const MACHINE_STATUS_LABELS = {
  online: "Çevrimiçi",
  offline: "Çevrimdışı",
};

export function decodeNotificationText(str) {
  if (!str || typeof str !== "string") return str || "";
  return str
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#x2F;/gi, "/")
    .replace(/&#x60;/gi, "`")
    .replace(/&#x3D;/gi, "=")
    .replace(/\bonline\b/gi, MACHINE_STATUS_LABELS.online)
    .replace(/\boffline\b/gi, MACHINE_STATUS_LABELS.offline);
}

function isAppPath(value) {
  return (
    typeof value === "string" &&
    value.startsWith("/") &&
    !value.startsWith("//")
  );
}

function isValidId(value) {
  const id = String(value || "").trim();
  return id && id !== "undefined" && id !== "null";
}

function entityEditPath(meta) {
  const entityId = meta.entityId || meta.orderId || meta.saleId;
  if (!isValidId(entityId)) return "";
  const entityType = meta.entityType;
  if (entityType === "order" || meta.orderId) return `/orders/edit/${entityId}`;
  if (entityType === "sale" || meta.saleId) {
    return `/device-sales/edit/${entityId}`;
  }
  return "";
}

export function getNotificationLink(notification) {
  const meta = notification?.metadata || {};
  const eventType = notification?.eventType || "";
  const trigger = meta.notificationEvent || "";

  const direct = meta.actionUrl || meta.sourcePage;
  if (isAppPath(direct)) {
    if (direct === "/orders" || direct === "/device-sales") {
      const deep = entityEditPath(meta);
      if (deep) return deep;
    }
    return direct;
  }

  if (eventType === "approval" || trigger.startsWith("approval.")) {
    if (meta.stage === "admin") return "/permission-approves";
    const deep = entityEditPath(meta);
    if (deep) return deep;
    return "/permission-approves";
  }

  if (eventType === "machine_status" || trigger.startsWith("machine.")) {
    return "/machine-monitor";
  }

  if (eventType === "machine_stop" || trigger === "shift.stop") {
    return meta.machineId ? "/machine-monitor" : "/machine-stop-logs";
  }

  if (eventType === "scrap_alert" || trigger === "scrap.alert") {
    return "/scrap-entry";
  }

  if (eventType === "stock_alert" || trigger === "stock.alert") {
    if (isValidId(meta.partId)) {
      return `/parts/${meta.partId}/stock-additions`;
    }
    return "/inventory";
  }

  if (eventType === "production" || trigger.startsWith("production.")) {
    if (isValidId(meta.lineId) && isValidId(meta.planId)) {
      return `/production-lines/view/${meta.lineId}`;
    }
    if (meta.machineId) return "/machine-monitor";
    return "/reports/production";
  }

  return "";
}

export function getNotificationNavigation(notification) {
  const pathname = getNotificationLink(notification);
  if (!pathname) return null;

  const meta = notification?.metadata || {};
  const state = {};
  if (pathname === "/machine-monitor" && isValidId(meta.machineId)) {
    state.machineId = String(meta.machineId);
  }

  return {
    pathname,
    state: Object.keys(state).length ? state : undefined,
  };
}

export function timeAgo(dateStr) {
  if (!dateStr) return "—";
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)} saniye önce`;
  if (diff < 3600) return `${Math.floor(diff / 60)} dakika önce`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} saat önce`;
  return `${Math.floor(diff / 86400)} gün önce`;
}

export const selectStyle = {
  background: "var(--m-card-background-color, #fff)",
  border: "1px solid #e2e5e8",
  borderRadius: "6px",
};

export const selectInner = {
  background: "transparent",
  border: "none",
  outline: "none",
  width: "100%",
  padding: "8px 12px",
  fontSize: "14px",
  color: "inherit",
  cursor: "pointer",
};
